/**
 * Context-aware salary period plausibility.
 *
 * Motivating case (2026-09-24): a Next Job Abroad listing for a mid-level "Player Support
 * Specialist" in Sofia, Bulgaria stated a bare "19020 EUR" with no period word anywhere on the
 * page. The extraction prompt's magnitude rule ("a bare figure at or above ~20,000 is annual")
 * let 19,020 through as *monthly*, and the model then invented "EUR 19,020 per month, as stated
 * in the listing" as the supporting quote. The indexer's own sanity check used the same hard
 * 20,000 cutoff, so nothing downstream caught it and the job page showed €19,020/mo for a
 * customer-support role in one of the EU's lowest-wage markets (the real figure is ~€1,585/mo).
 *
 * A single global threshold cannot decide this: €19,020/month is a plausible executive salary in
 * Switzerland and an absurd support-agent salary in Bulgaria. What a recruiter actually does when
 * reading a bare number is weigh it against what the role pays in that country. This module
 * encodes that judgement deterministically:
 *
 *   1. If the ad's own visible text labels the figure (per month / per year / hourly, in en/fi/sv),
 *      that label wins outright. The model's salaryDetails_* quote is NOT trusted for this, because
 *      it is exactly what got fabricated in the motivating case.
 *   2. Otherwise a "monthly" figure is reinterpreted as annual only when BOTH hold:
 *        a) read as monthly it exceeds a ceiling derived from the country's typical full-time
 *           gross monthly pay and the role's seniority (an executive in Switzerland gets a lot
 *           more headroom than a junior in Bulgaria), and
 *        b) read as annual (÷12) it lands at or above a floor for that same country, i.e. the
 *           annual reading is itself a believable wage there.
 *      When only (a) holds the number is implausible either way and is left alone for the
 *      existing magnitude-based sanitizers to deal with.
 *
 * Shared by the processor (post-extraction, with the visible page text available), the indexer
 * (re-run on every index so already-stored rows self-heal on the next reindex) and the Athena
 * export normalizer, so the three never disagree about what a stored "monthly" figure means.
 */

import { convertToEur } from '../../utils/currency/eur-exchange-rates';

export type SalaryPeriod = 'annual' | 'monthly' | 'daily' | 'hourly';

interface CountryWageProfile {
  /** Approximate full-time gross monthly pay for an ordinary mid-level role, in EUR. Coarse on
   *  purpose: it only sets the order of magnitude for the ceiling/floor, so ±30% doesn't matter. */
  typicalMonthlyGrossEur: number;
  /** Markets where ads customarily quote annual figures. A bare number in one of these is more
   *  likely to be annual, so the monthly ceiling is tightened. Nordic / most-of-continental-EU
   *  ads quote monthly and keep the full ceiling. */
  quotesAnnual?: boolean;
  /** ISO code assumed when the job carries no currency (a Warsaw ad quoting "26 000 – 32 000"
   *  with no currency stored is PLN, not EUR, and reading it as EUR would flip it wrongly). */
  currency?: string;
}

/**
 * Rough 2025-2026 gross monthly wage levels. Values are deliberately round; the decision logic
 * below only depends on the order of magnitude. Keys are the English country names the
 * extraction prompt asks for in location_en ("City, Country" / "Remote - Country").
 */
const COUNTRY_WAGE_PROFILES: Record<string, CountryWageProfile> = {
  Finland: { typicalMonthlyGrossEur: 3900, currency: 'EUR' },
  Sweden: { typicalMonthlyGrossEur: 3800, currency: 'SEK' },
  Norway: { typicalMonthlyGrossEur: 5000, currency: 'NOK' },
  Denmark: { typicalMonthlyGrossEur: 5500, currency: 'DKK' },
  Iceland: { typicalMonthlyGrossEur: 5500, currency: 'ISK' },
  Estonia: { typicalMonthlyGrossEur: 2000 },
  Latvia: { typicalMonthlyGrossEur: 1700 },
  Lithuania: { typicalMonthlyGrossEur: 2100 },
  Germany: { typicalMonthlyGrossEur: 4200, quotesAnnual: true },
  Austria: { typicalMonthlyGrossEur: 3800, quotesAnnual: true },
  Switzerland: { typicalMonthlyGrossEur: 7000, quotesAnnual: true, currency: 'CHF' },
  Netherlands: { typicalMonthlyGrossEur: 4000 },
  Belgium: { typicalMonthlyGrossEur: 3900 },
  Luxembourg: { typicalMonthlyGrossEur: 5500 },
  France: { typicalMonthlyGrossEur: 3200, quotesAnnual: true },
  'United Kingdom': { typicalMonthlyGrossEur: 3600, quotesAnnual: true, currency: 'GBP' },
  UK: { typicalMonthlyGrossEur: 3600, quotesAnnual: true, currency: 'GBP' },
  Ireland: { typicalMonthlyGrossEur: 4200, quotesAnnual: true },
  Spain: { typicalMonthlyGrossEur: 2500, quotesAnnual: true },
  Portugal: { typicalMonthlyGrossEur: 1600 },
  Italy: { typicalMonthlyGrossEur: 2500, quotesAnnual: true },
  Greece: { typicalMonthlyGrossEur: 1400 },
  Cyprus: { typicalMonthlyGrossEur: 2000 },
  Malta: { typicalMonthlyGrossEur: 2000, quotesAnnual: true },
  Poland: { typicalMonthlyGrossEur: 1900, currency: 'PLN' },
  Czechia: { typicalMonthlyGrossEur: 1900, currency: 'CZK' },
  'Czech Republic': { typicalMonthlyGrossEur: 1900, currency: 'CZK' },
  Slovakia: { typicalMonthlyGrossEur: 1600 },
  Hungary: { typicalMonthlyGrossEur: 1500, currency: 'HUF' },
  Romania: { typicalMonthlyGrossEur: 1500, currency: 'RON' },
  Bulgaria: { typicalMonthlyGrossEur: 1200, currency: 'EUR' },
  Croatia: { typicalMonthlyGrossEur: 1500 },
  Slovenia: { typicalMonthlyGrossEur: 2300 },
  Serbia: { typicalMonthlyGrossEur: 1000, currency: 'RSD' },
  Ukraine: { typicalMonthlyGrossEur: 600, currency: 'UAH' },
  Turkey: { typicalMonthlyGrossEur: 800, currency: 'TRY' },
  'United States': { typicalMonthlyGrossEur: 5000, quotesAnnual: true, currency: 'USD' },
  USA: { typicalMonthlyGrossEur: 5000, quotesAnnual: true, currency: 'USD' },
  Canada: { typicalMonthlyGrossEur: 4200, quotesAnnual: true, currency: 'CAD' },
  Australia: { typicalMonthlyGrossEur: 4800, quotesAnnual: true, currency: 'AUD' },
  'New Zealand': { typicalMonthlyGrossEur: 4000, quotesAnnual: true, currency: 'NZD' },
  Japan: { typicalMonthlyGrossEur: 2800, quotesAnnual: true, currency: 'JPY' },
  Singapore: { typicalMonthlyGrossEur: 4500, quotesAnnual: true, currency: 'SGD' },
  India: { typicalMonthlyGrossEur: 700, quotesAnnual: true, currency: 'INR' },
  Philippines: { typicalMonthlyGrossEur: 450, currency: 'PHP' },
  'South Africa': { typicalMonthlyGrossEur: 1300, quotesAnnual: true, currency: 'ZAR' },
  'United Arab Emirates': { typicalMonthlyGrossEur: 3500, currency: 'AED' },
  Israel: { typicalMonthlyGrossEur: 3200, currency: 'ILS' },
  Brazil: { typicalMonthlyGrossEur: 700, currency: 'BRL' },
  Mexico: { typicalMonthlyGrossEur: 700, currency: 'MXN' },
  Colombia: { typicalMonthlyGrossEur: 500, currency: 'COP' },
};

/** Used when the country is unknown, a multi-country remote scope, or not in the table. Set to
 *  the Finnish level because the vast majority of this pipeline's listings are Finnish. */
const DEFAULT_WAGE_PROFILE: CountryWageProfile = { typicalMonthlyGrossEur: 3900, currency: 'EUR' };

/**
 * How many multiples of the country's typical monthly wage a bare "monthly" figure may reach
 * before it stops being believable for that seniority. A specialist doctor or senior engineer in
 * Finland can legitimately be quoted 3x-4x the typical wage per month; a junior cannot.
 */
const ROLE_LEVEL_CEILING_MULTIPLIER: Record<string, number> = {
  Intern: 2,
  Junior: 3,
  'Mid-Level': 4,
  Senior: 5,
  Expert: 6,
  Manager: 6,
  Director: 8,
  Executive: 12,
};
const DEFAULT_CEILING_MULTIPLIER = ROLE_LEVEL_CEILING_MULTIPLIER['Mid-Level'];

/** Bare numbers in annual-quoting markets get a tighter monthly ceiling. */
const ANNUAL_MARKET_CEILING_FACTOR = 0.6;

/**
 * The annual reading (÷12) must be at least this share of the country's typical monthly wage to
 * count as a believable wage there. Guards against flipping a genuine (if high) Finnish monthly
 * figure into an annual reading that would put the job below any realistic full-time pay.
 */
const ANNUAL_READING_FLOOR_SHARE = 0.4;

/** Absolute floor on the annual reading, so a low-wage country profile can't accept nonsense. */
const ANNUAL_READING_ABSOLUTE_FLOOR_EUR = 300;


/**
 * Extracts the country component from a location_en entry of the form "City, Country",
 * "Country" or "Remote - Country". Returns undefined for multi-country scopes ("Remote - Global",
 * "Remote - Europe", "EMEA") so they fall through to the default profile.
 */
export function extractCountryFromLocationString(location: unknown): string | undefined {
  if (typeof location !== 'string') return undefined;
  let s = location.trim();
  if (!s) return undefined;
  const remote = s.match(/^remote\s*-\s*(.+)$/i);
  if (remote) s = remote[1].trim();
  const parts = s.split(',').map((p) => p.trim()).filter(Boolean);
  const candidate = parts[parts.length - 1];
  if (!candidate) return undefined;
  if (/^(global|worldwide|europe|eu|emea|nordics|nordic|apac|americas|latam|asia|africa)$/i.test(candidate)) {
    return undefined;
  }
  return candidate;
}

function wageProfileFor(country: string | undefined): CountryWageProfile {
  if (!country) return DEFAULT_WAGE_PROFILE;
  return COUNTRY_WAGE_PROFILES[country] ?? DEFAULT_WAGE_PROFILE;
}

export interface SalaryPeriodContext {
  country?: string;
  roleLevel?: string;
  currency?: string;
}

/**
 * The highest monthly gross figure (in EUR) still believable for this country and seniority when
 * the ad gives no period label. Exported for tests and for the audit CLI's report.
 */
export function monthlySalaryCeilingEur(ctx: SalaryPeriodContext): number {
  const profile = wageProfileFor(ctx.country);
  const multiplier =
    (ctx.roleLevel && ROLE_LEVEL_CEILING_MULTIPLIER[ctx.roleLevel]) || DEFAULT_CEILING_MULTIPLIER;
  const marketFactor = profile.quotesAnnual ? ANNUAL_MARKET_CEILING_FACTOR : 1;
  return profile.typicalMonthlyGrossEur * multiplier * marketFactor;
}

/** The lowest monthly figure (in EUR) that an annual reading must reach to be believable. */
export function annualReadingFloorEur(ctx: SalaryPeriodContext): number {
  const profile = wageProfileFor(ctx.country);
  return Math.max(ANNUAL_READING_ABSOLUTE_FLOOR_EUR, profile.typicalMonthlyGrossEur * ANNUAL_READING_FLOOR_SHARE);
}

/** Unknown currency -> undefined, which disables the contextual flip: a weak currency read at
 *  1:1 would look like an enormous EUR figure and get flipped for the wrong reason. */
function toEur(amount: number, currency: string | undefined): number | undefined {
  return convertToEur(amount, currency);
}

/**
 * Pure decision: given a "monthly" range and its context, should it be read as annual instead?
 * `amountMax` is the larger end of the stated range and `amountMin` the smaller (or the same
 * figure for a single value), both in the ad's own currency. Both ends must survive the annual
 * reading: a range like "$22,916 - $31,250" whose low end would become sub-minimum-wage pay is
 * left alone, since that shape is what an annual range already divided by 12 looks like.
 */
export function shouldReinterpretMonthlyAsAnnual(
  amountMax: number,
  ctx: SalaryPeriodContext,
  amountMin: number = amountMax,
): boolean {
  if (!Number.isFinite(amountMax) || amountMax <= 0 || !Number.isFinite(amountMin) || amountMin <= 0) return false;
  const currency = ctx.currency || wageProfileFor(ctx.country).currency;
  const maxEur = toEur(amountMax, currency);
  const minEur = toEur(amountMin, currency);
  if (maxEur === undefined || minEur === undefined) return false;
  if (maxEur <= monthlySalaryCeilingEur(ctx)) return false;
  const floor = annualReadingFloorEur(ctx);
  return maxEur / 12 >= floor && minEur / 12 >= floor;
}

/**
 * Period-word patterns. Written with `\b`, then compiled through unicodeWordBoundaries() because
 * JavaScript's `\b` only knows ASCII letters: `\bårslön\b` never matches (Å is "not a word
 * character"), and `\bkk\b` would happily match inside "kkä". Unicode-aware lookarounds fix both.
 */
function unicodeWordBoundaries(source: string): RegExp {
  const boundary = '(?:(?<![\\p{L}\\p{N}])(?=[\\p{L}\\p{N}])|(?<=[\\p{L}\\p{N}])(?![\\p{L}\\p{N}]))';
  return new RegExp(source.replace(/\\b/g, boundary), 'iu');
}
const MONTHLY_MARKERS = unicodeWordBoundaries(
  String.raw`(\/\s*(kk|mo|month|mth|mån|månad)\b|\bper\s+month\b|\bmonthly\b|\ba\s+month\b|\bkuukaudessa\b|\bkuukausipalkka\b|\bkk\b|\bi\s+månaden\b|\bper\s+månad\b|\bmånadslön\b|\bmånadsvis\b)`,
);
const ANNUAL_MARKERS = unicodeWordBoundaries(
  String.raw`(\/\s*(yr|year|annum|år|vuosi|v|a)\b|\bper\s+(year|annum)\b|\b(annual(ly)?|yearly)\b|\bp\.\s*a\.|\ba\s+year\b|\bvuodessa\b|\bvuosipalkka\b|\bvuosiansio\b|\bper\s+år\b|\bårslön\b|\bårligen\b|\bom\s+året\b)`,
);
const HOURLY_MARKERS = unicodeWordBoundaries(
  String.raw`(\/\s*(h|hr|hour|t|tim|tunti)\b|\bper\s+hour\b|\bhourly\b|\ban\s+hour\b|\btunnissa\b|\btuntipalkka\b|\bper\s+timme\b|\bi\s+timmen\b|\btimlön\b)`,
);

type ExplicitPeriod = 'monthly' | 'annual' | 'hourly';
const MARKERS: Array<[ExplicitPeriod, RegExp]> = [
  ['hourly', HOURLY_MARKERS],
  ['monthly', MONTHLY_MARKERS],
  ['annual', ANNUAL_MARKERS],
];

/** The period marker that appears earliest in `window`, so "2 400 €/kk (15 €/h)" attaches "/kk"
 *  to 2400 rather than the later "/h". */
function earliestMarker(window: string): ExplicitPeriod | undefined {
  let best: { period: ExplicitPeriod; index: number } | undefined;
  for (const [period, re] of MARKERS) {
    const m = re.exec(window);
    if (m && (!best || m.index < best.index)) best = { period, index: m.index };
  }
  return best?.period;
}

/** Every textual spelling of an integer amount the ad might have used (grouping variants). */
function amountSpellings(amount: number): string[] {
  const raw = Math.round(amount).toString();
  const group = (sep: string) => raw.replace(/\B(?=(\d{3})+(?!\d))/g, sep);
  return Array.from(new Set([raw, group(' '), group('.'), group(','), group("'")]));
}

/**
 * Finds the pay period the ad's own text attaches to `amount`, if any. Looks only at a short
 * window after each occurrence of the number (and at the ~30 characters before it, for phrasings
 * like "monthly salary of 3 500 €"). Returns undefined when the number is unlabeled or absent.
 */
export function findExplicitPeriodForAmountInText(
  text: string | undefined,
  amount: number,
): 'monthly' | 'annual' | 'hourly' | undefined {
  if (!text || !Number.isFinite(amount)) return undefined;
  // Nordic thin/no-break spaces used as thousands separators.
  const normalized = text.replace(/[   ]/g, ' ');
  for (const spelling of amountSpellings(amount)) {
    let from = 0;
    for (;;) {
      const idx = normalized.indexOf(spelling, from);
      if (idx === -1) break;
      from = idx + spelling.length;
      // Skip if the match sits inside a longer digit run (e.g. "19020" inside "190200").
      const charBefore = normalized[idx - 1];
      const charAfter = normalized[idx + spelling.length];
      if ((charBefore && /\d/.test(charBefore)) || (charAfter && /\d/.test(charAfter))) continue;
      const labelAfter = earliestMarker(normalized.slice(from, from + 45));
      if (labelAfter) return labelAfter;
      const labelBefore = earliestMarker(normalized.slice(Math.max(0, idx - 30), idx));
      if (labelBefore) return labelBefore;
    }
  }
  return undefined;
}

/**
 * Every number in `text` (grouped, decimal, or "127.5K"-style), as plain values. Used to detect
 * the "annual figure already divided by 12" pattern below.
 */
function numbersInText(text: string): number[] {
  const normalized = text.replace(/[\u00A0\u202F\u2009]/g, ' ');
  const out: number[] = [];
  const re = /\d[\d\s.,']*\d(?:\s*[kK](?![\p{L}]))?|\d(?:\s*[kK](?![\p{L}]))?/gu;
  for (const m of normalized.matchAll(re)) {
    let token = m[0];
    const thousands = /[kK]$/.test(token.trim());
    token = token.replace(/[kK]$/, '').replace(/[\s']/g, '');
    // A trailing separator followed by exactly 1-2 digits is a decimal part ("1,234.56", "12,00");
    // every other separator is thousands grouping ("275,000", "3.500", "6 501").
    const dec = token.match(/^(\d[\d.,]*?)[.,](\d{1,2})$/);
    const value = dec
      ? Number(dec[1].replace(/[.,]/g, '')) + Number(`0.${dec[2]}`)
      : Number(token.replace(/[.,]/g, ''));
    if (!Number.isFinite(value)) continue;
    out.push(thousands ? value * 1000 : value);
  }
  return out;
}

/** True when `text` contains a figure within 1% of `amount` × 12. */
export function textContainsTwelvefold(text: string | undefined, amount: number): boolean {
  if (!text || !Number.isFinite(amount) || amount <= 0) return false;
  const target = amount * 12;
  return numbersInText(text).some((n) => Math.abs(n - target) <= target * 0.01);
}

export interface SalaryPeriodJobLike {
  salaryRangeMin?: unknown;
  salaryRangeMax?: unknown;
  salaryPeriod?: unknown;
  currency?: unknown;
  location_en?: unknown;
  normalisedRoleLevel_en?: unknown;
  salaryDetails_en?: unknown;
  salaryDetails_fi?: unknown;
  salaryDetails_sv?: unknown;
}

function finiteOrUndefined(v: unknown): number | undefined {
  if (typeof v === 'string' && v.trim() === '') return undefined;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : undefined;
}

export function salaryPeriodContextFromJob(job: SalaryPeriodJobLike): SalaryPeriodContext {
  const loc = Array.isArray(job.location_en) ? job.location_en[0] : job.location_en;
  return {
    country: extractCountryFromLocationString(loc),
    roleLevel: typeof job.normalisedRoleLevel_en === 'string' ? job.normalisedRoleLevel_en : undefined,
    currency: typeof job.currency === 'string' && job.currency.trim() ? job.currency : undefined,
  };
}

function salaryDetailsStrings(job: SalaryPeriodJobLike): string[] {
  return [job.salaryDetails_en, job.salaryDetails_fi, job.salaryDetails_sv].filter(
    (v): v is string => typeof v === 'string' && v.trim().length > 0,
  );
}

export interface ResolvedSalaryPeriod {
  period: SalaryPeriod;
  /** Why the stored period was changed, or undefined when it was kept. */
  reason?:
    | 'explicit_in_source_text'
    | 'explicit_annual_in_salary_details'
    | 'implausible_monthly_for_country_and_role';
}

/**
 * Decides the pay period a stored salary should actually carry. Does not mutate `job`.
 *
 * `visibleText` is the ad's own visible text when the caller has it (the processor does); the
 * indexer and export paths pass nothing. In every path the model's salaryDetails_* quote is used
 * for two things only: (a) an ANNUAL label attached to the stored figure (the model quoting
 * "65,000 - 81,000 EUR per year" while labeling the period monthly is a self-contradiction to
 * resolve in the quote's favour), and (b) an annual figure ~12x the stored one, which means the
 * model already divided an annual range by 12 and the stored "monthly" is right. A MONTHLY label
 * in the quote is never trusted: that is exactly what got fabricated in the motivating case.
 */
export function resolvePlausibleSalaryPeriod(
  job: SalaryPeriodJobLike,
  visibleText?: string,
): ResolvedSalaryPeriod | undefined {
  const min = finiteOrUndefined(job.salaryRangeMin);
  const max = finiteOrUndefined(job.salaryRangeMax);
  const amount = max ?? min;
  if (amount === undefined || amount <= 0) return undefined;
  const low = Math.min(min ?? amount, max ?? amount);
  const high = Math.max(min ?? amount, max ?? amount);
  const stored = typeof job.salaryPeriod === 'string' ? (job.salaryPeriod as SalaryPeriod) : undefined;

  if (visibleText) {
    const explicit = findExplicitPeriodForAmountInText(visibleText, high) ?? findExplicitPeriodForAmountInText(visibleText, low);
    if (explicit) {
      return explicit === stored
        ? { period: explicit }
        : { period: explicit, reason: 'explicit_in_source_text' };
    }
  }

  if (stored !== 'monthly') return stored ? { period: stored } : undefined;

  const details = salaryDetailsStrings(job);
  const alreadyDividedBy12 = [visibleText, ...details].some(
    (t) => textContainsTwelvefold(t, high) || textContainsTwelvefold(t, low),
  );
  if (alreadyDividedBy12) return { period: 'monthly' };

  if (details.some((t) => findExplicitPeriodForAmountInText(t, high) === 'annual')) {
    return { period: 'annual', reason: 'explicit_annual_in_salary_details' };
  }

  if (shouldReinterpretMonthlyAsAnnual(high, salaryPeriodContextFromJob(job), low)) {
    return { period: 'annual', reason: 'implausible_monthly_for_country_and_role' };
  }
  return { period: stored };
}

/**
 * Mutating convenience for pipeline steps: applies resolvePlausibleSalaryPeriod to the job's
 * salaryPeriod field and returns the change reason when one was made.
 */
export function applyPlausibleSalaryPeriod(
  job: SalaryPeriodJobLike,
  visibleText?: string,
): ResolvedSalaryPeriod['reason'] | undefined {
  const resolved = resolvePlausibleSalaryPeriod(job, visibleText);
  if (!resolved || !resolved.reason) return undefined;
  job.salaryPeriod = resolved.period;
  return resolved.reason;
}
